"use client";

import { io } from "socket.io-client";
import { getSession } from "next-auth/react";

// The socket is created synchronously (browser-only) at module load so that the
// exported `socket` binding is ALWAYS a defined Socket instance on the client,
// from the very first render. Previously it was assigned only after an async
// `getSession()` resolved, so on the first render after login `socket` was
// `undefined` — and any effect cleanup calling `socket.off(...)` (e.g. when the
// login -> inbox redirect cancels/remounts the route) crashed the whole React
// tree with "Cannot read properties of undefined (reading 'off')".
//
// Authentication is resolved lazily via the `auth` callback, which socket.io
// invokes before every (re)connection attempt, so we never have to block on the
// session before the socket exists. The server reads `socket.handshake.auth.token`.

let socket;

if (typeof window !== "undefined") {
  socket = io({
    path: "/api/socket",
    auth: (cb) => {
      getSession()
        .then((session) => cb({ token: session || null }))
        .catch(() => cb({ token: null }));
    },
  });

  socket.on("connect", () => {
    // Connection lifecycle is intentionally quiet; socket.io handles retries.
  });

  socket.on("disconnect", () => {
    // Disconnections are expected during navigation, HMR, and network recovery.
  });
}

// Kept for backward compatibility with existing callers. The socket is now
// created at import time, so this just returns the singleton (and ensures it is
// connecting if it somehow got disconnected).
export function initializeSocket() {
  if (socket && !socket.connected) {
    socket.connect();
  }
  return socket;
}

export { socket };
